/**
 * Headless deadline-urgency helpers.
 *
 * Maps an ISO date to one of four urgency bands so callers (UI cells,
 * picker rows, future digest tiles) can pick consistent visual treatment
 * without re-deriving the rules.
 *
 * Bands match the repo's existing colour conventions:
 *   - comfortable  →  text-muted-foreground (default repo non-urgent)
 *   - approaching  →  text-amber-600 (matches comms timeline / alerts)
 *   - due-today    →  text-amber-700 font-medium + warning icon
 *   - overdue      →  text-destructive font-medium + warning icon
 *                     (matches TicketCard / OpportunityCard overdue)
 *
 * Visual class selection stays in the shell — these helpers stay
 * platform-neutral and return the band only.
 */

import { differenceInCalendarDays } from "date-fns";
import { currentTime } from "./current-time";

export type DeadlineUrgency =
  "comfortable" | "approaching" | "due-today" | "overdue";

/**
 * Map a due date (ISO yyyy-mm-dd or full ISO timestamp) to its urgency
 * band relative to ``now``.
 *
 * Threshold for "approaching" is ≤7 days. Chosen to fit the standard
 * monthly cycle (opens 1st of month, due last day → roughly a week of
 * elevated attention at the tail).
 */
export function deadlineUrgency(
  dueDate: string,
  now: Date = currentTime(),
): DeadlineUrgency {
  const due = new Date(dueDate);
  const d = differenceInCalendarDays(due, now);
  if (d < 0) return "overdue";
  if (d === 0) return "due-today";
  if (d <= 7) return "approaching";
  return "comfortable";
}

/**
 * Render the "(in N days)" / "due today" / "N days overdue" label
 * suffix. Pairs with ``deadlineUrgency`` — keep them aligned so the
 * label matches the visual treatment.
 */
export function deadlineSuffix(
  dueDate: string,
  now: Date = currentTime(),
): string {
  const due = new Date(dueDate);
  const d = differenceInCalendarDays(due, now);
  if (d < 0) return `${Math.abs(d)} ${plural("day", Math.abs(d))} overdue`;
  if (d === 0) return "due today";
  return `in ${d} ${plural("day", d)}`;
}

/** "1 day" / "2 days" — exported so callers wording their own day counts
 *  (the CSR board's card reason lines) agree with `deadlineSuffix` instead
 *  of hand-rolling a count that reads "1 days". */
export function plural(word: string, n: number): string {
  return n === 1 ? word : `${word}s`;
}

/**
 * Whether a due date has passed, comparing calendar days only — a date due
 * later today is never overdue, matching `deadlineUrgency`'s "due-today"
 * band. This is the shared predicate for "is this job/ticket/milestone
 * overdue" across the board badges, tickets rail, and stage-milestone rows;
 * a caller that re-derives this with `new Date(x).getTime() < Date.now()`
 * is comparing a wall-clock instant instead of a calendar date and will
 * flag anything due today as overdue from midnight. The backend learned
 * this the hard way — see `effective_deadline` in
 * `modules/work_visibility_module/actions/_helpers.py`, which judges
 * lateness against end-of-due-day for the same reason.
 */
export function isOverdue(dueDate: string, now: Date = currentTime()): boolean {
  return deadlineUrgency(dueDate, now) === "overdue";
}

/**
 * Whether a due date falls on today's calendar day. Shares
 * `deadlineUrgency`'s calendar-day comparison, so this and `isOverdue`
 * can never both be true for the same date/`now` pair.
 */
export function isDueToday(
  dueDate: string,
  now: Date = currentTime(),
): boolean {
  return deadlineUrgency(dueDate, now) === "due-today";
}
